import type { CSSProperties, ReactNode } from "react";
import "./platform-tier-card.css";

export type PlatformTierCode = "MERO" | "MERO_PLUS" | "MERO_GOLD" | "MERO_ELITE";

interface TierCardMetadata {
  code: PlatformTierCode;
  name: string;
  artwork: string;
  footerBackground: string;
  foreground: string;
  accent: string;
}

const tierCards: Record<PlatformTierCode, TierCardMetadata> = {
  MERO: {
    code: "MERO",
    name: "Mero",
    artwork: "/brand/tiers/tier-card-mero.png",
    footerBackground: "#F7F5F0",
    foreground: "#122937",
    accent: "#C8102E",
  },
  MERO_PLUS: {
    code: "MERO_PLUS",
    name: "Mero Plus",
    artwork: "/brand/tiers/tier-card-mero-plus.png",
    footerBackground: "#C8102E",
    foreground: "#FFFFFF",
    accent: "#F5A623",
  },
  MERO_GOLD: {
    code: "MERO_GOLD",
    name: "Mero Gold",
    artwork: "/brand/tiers/tier-card-mero-gold.png",
    footerBackground: "#F5A623",
    foreground: "#122937",
    accent: "#122937",
  },
  MERO_ELITE: {
    code: "MERO_ELITE",
    name: "Mero Elite",
    artwork: "/brand/tiers/tier-card-mero-elite.png",
    footerBackground: "#122937",
    foreground: "#FFFFFF",
    accent: "#F5A623",
  },
};

export function normalizePlatformTierCode(value?: string | null): PlatformTierCode {
  const normalized = value?.trim().toUpperCase().replaceAll("-", "_");
  return normalized && normalized in tierCards ? (normalized as PlatformTierCode) : "MERO";
}

export function platformTierCardMetadata(value?: string | null): TierCardMetadata {
  return tierCards[normalizePlatformTierCode(value)];
}

export interface TierCardMember {
  name: string;
  memberId: string;
  memberSince?: string | null;
}

interface PlatformTierCardProps {
  tierCode?: string | null | undefined;
  tierName?: string | null | undefined;
  member?: TierCardMember | undefined;
  children?: ReactNode;
  className?: string | undefined;
  variant?: "portrait" | "horizontal" | "thumbnail" | undefined;
  loading?: "eager" | "lazy" | undefined;
}

export function PlatformTierCard({
  tierCode,
  tierName,
  member,
  children,
  className = "",
  variant = "portrait",
  loading = "lazy",
}: PlatformTierCardProps) {
  const card = platformTierCardMetadata(tierCode);
  const label = tierName?.trim() || card.name;
  return (
    <article
      className={`mp-tier-card mp-tier-card--${card.code.toLowerCase().replaceAll("_", "-")} mp-tier-card--${variant} ${className}`.trim()}
      style={
        {
          "--mp-tier-footer": card.footerBackground,
          "--mp-tier-fg": card.foreground,
          "--mp-tier-accent": card.accent,
        } as CSSProperties
      }
      aria-label={`${label} tier card${member ? ` for ${member.name}` : ""}`}
    >
      <div className="mp-tier-card__art">
        <img
          src={card.artwork}
          alt={`${label} tier card`}
          width="1027"
          height="1531"
          loading={loading}
          decoding="async"
        />
      </div>
      {member && (
        <footer className="mp-tier-card__member">
          <strong title={member.name}>{member.name}</strong>
          <dl>
            <div>
              <dt>Member ID</dt>
              <dd title={member.memberId}>{member.memberId}</dd>
            </div>
            <div>
              <dt>Member since</dt>
              <dd>{member.memberSince || "—"}</dd>
            </div>
            <span aria-hidden="true">✦</span>
          </dl>
        </footer>
      )}
      {children && <div className="mp-tier-card__content">{children}</div>}
    </article>
  );
}
